import { useState } from 'react';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { cn } from '../../lib/utils';
import {
  buildCalendarWeeks,
  monthLabel,
  parseIso,
  shiftMonth,
  toIso,
} from '../../lib/dateRangePicker';

// A single-DATE picker, built on the SAME pure calendar core as the shared
// DateRangePicker (src/lib/dateRangePicker) rather than a second implementation
// — so the two agree about month layout, week start and ISO parsing by
// construction, and neither needs a date library.
//
// Emits 'yyyy-mm-dd', matching the range picker's shape.

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

function todayIso(): string {
  const now = new Date();
  return toIso(now.getFullYear(), now.getMonth() + 1, now.getDate());
}

export function formatDateLabel(iso: string | null | undefined): string {
  const parsed = parseIso(iso);
  if (!parsed) return 'Pick a date';
  const d = new Date(parsed.y, parsed.m - 1, parsed.d);
  return d.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function DatePicker({
  value,
  onChange,
  minIso,
  ariaLabel = 'Date',
  id,
}: {
  value: string | null | undefined;
  onChange: (iso: string) => void;
  /** Days before this are not selectable (e.g. an expiry can't be in the past). */
  minIso?: string;
  ariaLabel?: string;
  id?: string;
}) {
  const [open, setOpen] = useState(false);
  const initial = parseIso(value) ?? parseIso(todayIso())!;
  const [view, setView] = useState<{ y: number; m: number }>({
    y: initial.y,
    m: initial.m,
  });
  const weeks = buildCalendarWeeks(view.y, view.m);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          id={id}
          type="button"
          aria-label={ariaLabel}
          className="flex h-9 w-full items-center gap-2 rounded-md border border-input bg-background px-3 text-left text-sm shadow-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kyck-500"
        >
          <CalendarDays className="h-4 w-4 shrink-0 text-slate-500" aria-hidden />
          <span className={cn(!value && 'text-slate-400')}>{formatDateLabel(value)}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-3" align="start">
        <div className="mb-2 flex items-center justify-between gap-2">
          <button
            type="button"
            aria-label="Previous month"
            className="rounded p-1 hover:bg-slate-100"
            onClick={() => setView(shiftMonth(view.y, view.m, -1))}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <div className="text-sm font-medium">{monthLabel(view.y, view.m)}</div>
          <button
            type="button"
            aria-label="Next month"
            className="rounded p-1 hover:bg-slate-100"
            onClick={() => setView(shiftMonth(view.y, view.m, 1))}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
        <div className="grid grid-cols-7 gap-0.5 text-center text-[11px] text-slate-500">
          {WEEKDAYS.map((d, i) => (
            <div key={`${d}-${i}`} className="py-1">
              {d}
            </div>
          ))}
        </div>
        {weeks.map((week, wi) => (
          <div key={wi} className="grid grid-cols-7 gap-0.5">
            {week.map((cell) => {
              const selected = cell.iso === value;
              // String comparison is safe on 'yyyy-mm-dd' and avoids any
              // timezone question about what "before today" means.
              const disabled = minIso ? cell.iso < minIso : false;
              return (
                <button
                  key={cell.iso}
                  type="button"
                  disabled={disabled}
                  onClick={() => {
                    onChange(cell.iso);
                    setOpen(false);
                  }}
                  className={cn(
                    'h-8 rounded text-xs',
                    !cell.inMonth && 'text-slate-300',
                    disabled && 'cursor-not-allowed text-slate-300 hover:bg-transparent',
                    !disabled && 'hover:bg-slate-100',
                    selected && 'bg-kyck-600 font-semibold text-white hover:bg-kyck-600',
                  )}
                >
                  {cell.day}
                </button>
              );
            })}
          </div>
        ))}
      </PopoverContent>
    </Popover>
  );
}
